import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./loginpagestyles.module.css";
import TwoFactorInput from "./TwoFactorInput";
import logo from "../assets/the.rENT_footer.jpg";
import { API_BASE } from '../config';
import { useTranslation } from '../translations';
import { zapiszFlagePrzerwy } from '../utils/kontoWPrzerwie';
import { Modal } from '../components/ui/Modal';

function Login({ onLogin }) {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState("");

  const [loading, setLoading] = useState(false);

  // Stan 2FA — pendingToken tylko w React state (nigdy w localStorage)
  const [pendingFor2FA, setPendingFor2FA] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);

  const navigate = useNavigate();

  // Jeśli użytkownik ma już aktywną sesję — przekieruj od razu bez pokazywania formularza
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/whoami`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data && data.userId) {
            // Zawsze /home — panel super-admina to jedno z narzedzi w menu, nie domyslny
            // ekran po zalogowaniu (patrz analogiczny komentarz w handleLoginSuccess nizej).
            navigate('/home', { replace: true });
          }
        }
      } catch (_) {
        // brak połączenia — zostań na stronie logowania
      }
    };
    checkExistingSession();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setUsernameError(false);
    setPasswordError(false);
    setSuccessMessage("");

    if (!username || !password) {
      if (!username) setUsernameError(true);
      if (!password) setPasswordError(true);
      setError(t('login.allFieldsRequired'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include', // <-- dodane, aby ciasteczka sid/csrf zostały zapisane
        body: JSON.stringify({ login: username, passwo: password }),
      });

      const data = await response.json().catch(() => ({}));
      // jeśli backend zwróci csrfToken -> zapisz w localStorage (synchronizacja)
      if (data && data.csrfToken) {
        try { localStorage.setItem('csrfToken', data.csrfToken); } catch (_) {}
      }

      if (response.status === 429) {
        setError(data.message || t('login.tooManyAttempts'));
        setLoading(false);
        return;
      }

      if (!response.ok) {
        setError(data.message || t('login.genericError'));
        setLoading(false);
        return;
      }

      // Użytkownik ma włączone 2FA — przekieruj do ekranu TOTP
      if (data.requires2FA && data.pendingToken) {
        setPendingToken(data.pendingToken);
        setPendingFor2FA(true);
        setLoading(false);
        return;
      }

      // Sukces logowania
      handleLoginSuccess(data);
    } catch (err) {
      setError(t('login.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (data) => {
    if (data.csrfToken) {
      try { localStorage.setItem('csrfToken', data.csrfToken); } catch (_) {}
    }
    const userId = String(data.userId);
    localStorage.setItem("userId", userId);
    localStorage.setItem("isLoggedIn", "true");
    const isAdmin =
      (data.user && (data.user.isAdmin === true || data.user.is_admin === 1)) ||
      data.isAdmin === true ||
      data.is_admin === 1 || false;
    localStorage.setItem("isAdmin", isAdmin ? "1" : "0");
    const isSuperAdmin = (data.user && data.user.isSuperAdmin === true) || false;
    localStorage.setItem("isSuperAdmin", isSuperAdmin ? "1" : "0");
    const organizationId = data.user?.organizationId || '';
    localStorage.setItem("organizationId", organizationId);
    const subscriptionPlan = data.user?.subscriptionPlan || 'basic';
    localStorage.setItem("subscriptionPlan", subscriptionPlan);
    localStorage.setItem("isDemo", data.user?.isDemo ? '1' : '0');
    zapiszFlagePrzerwy(localStorage, data.user);
    localStorage.setItem("userFirstName", data.user?.firstname || '');
    localStorage.setItem("userLastName", data.user?.lastname || '');
    localStorage.setItem("organizationName", data.user?.organizationName || '');
    setSuccessMessage(t('login.success'));
    if (typeof onLogin === "function") {
      onLogin({
        userId,
        isAdmin,
        isSuperAdmin,
        organizationId,
        username,
        ...data.user
      });
    }
    // Zawsze /home, super-admin nie wyjatkiem — to byla przyczyna „czasami loguje mnie
    // od razu w panelu admina": ten sam uzytkownik ladowal w innym miejscu w zaleznosci
    // od tego, ktora z trzech tras mintujacych sesje (login/2FA verify/2FA recover) go
    // obslugiwala. `data.redirectUrl` zostaje jako nadrzedny wybor backendu (dziś zawsze
    // '/home' — patrz routes/auth.js), `isSuperAdmin` juz nie wplywa na trase.
    navigate(data.redirectUrl || "/home");
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage("");
    setResetLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setResetMessage(data.message || t('login.resetSentDefault'));
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetEmail("");
          setResetMessage("");
        }, 5000);
      } else {
        setResetMessage(data.error || t('login.resetError'));
      }
    } catch (err) {
      setResetMessage(t('login.connectionError'));
    } finally {
      setResetLoading(false);
    }
  };

  if (pendingFor2FA) {
    return (
      <TwoFactorInput
        pendingToken={pendingToken}
        onSuccess={(data) => {
          setPendingFor2FA(false);
          setPendingToken(null);
          handleLoginSuccess(data);
        }}
        onCancel={() => {
          setPendingFor2FA(false);
          setPendingToken(null);
        }}
      />
    );
  }

  return (
    <div className={styles.loginPage}>
      <div className={styles.formContainer}>
        <div className={styles.brandHeader}>
          <img src={logo} alt="the.rENT" className={styles.brandLogo} />
          <span className={styles.brandName}>the.rENT</span>
        </div>
        <p className={styles.brandTagline}>{t('login.tagline')}</p>

        <h2>{t('login.heading')}</h2>
        <p className={styles.formSubtitle}>{t('login.subtitle')}</p>
        <form onSubmit={handleSubmit}>
          <label htmlFor="login-username-field" style={{ display: 'none' }}>{t('login.usernamePlaceholder')}</label>
          {/* Login to zwykle e-mail (onboarding.js: login = email), a serwer porównuje go znak w znak.
              Bez tych atrybutów iOS zmieniał pierwszą literę na wielką („jan@firma.pl” -> „Jan@firma.pl”)
              i logowanie z iPhone'a i iPada kończyło się „Błędne dane!” (symulator iOS 26.5, 27.09.2026). */}
          <input
            id="login-username-field"
            type="text"
            placeholder={t('login.usernamePlaceholder')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="username"
            inputMode="email"
            data-cy="login-username"
            className={`${styles.inputField} ${usernameError ? styles.inputError : ""}`}
          />
          <label htmlFor="login-password-field" style={{ display: 'none' }}>{t('login.passwordPlaceholder')}</label>
          <input
            id="login-password-field"
            type="password"
            placeholder={t('login.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            data-cy="login-password"
            className={`${styles.inputField} ${passwordError ? styles.inputError : ""}`}
          />
          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
            data-cy="login-submit"
          >
            {loading ? t('login.submitting') : t('login.submit')}
          </button>
        </form>

        <button
          type="button"
          className={styles.forgotPasswordLink}
          onClick={() => setShowForgotPassword(true)}
        >
          {t('login.forgotPassword')}
        </button>

        {successMessage && <p className={styles.successMessage}>{successMessage}</p>}
        {error && <p className={styles.errorMessage}>{error}</p>}
      </div>

      <Modal
        open={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        title={t('login.resetTitle')}
        size="sm"
        data-cy="forgot-password-modal"
      >
        <form onSubmit={handleForgotPassword}>
          <p className={styles.modalDescription}>{t('login.resetDescription')}</p>
          <label htmlFor="login-reset-email" style={{ display: 'none' }}>{t('login.resetEmailPlaceholder')}</label>
          <input
            id="login-reset-email"
            type="email"
            placeholder={t('login.resetEmailPlaceholder')}
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            className={styles.inputField}
            required
          />
          {resetMessage && <p className={styles.resetMessage}>{resetMessage}</p>}
          <button
            type="submit"
            className={styles.submitButton}
            disabled={resetLoading}
          >
            {resetLoading ? t('login.resetSubmitting') : t('login.resetSubmit')}
          </button>
        </form>
      </Modal>
    </div>
  );
}

export default Login;
